import React, { FC, useMemo } from 'react';
import { IconButton, TableCell, TableRow } from '@material-ui/core';
import { groupBy } from 'lodash';

import { useAppData } from 'src/providers';

import { ReportsCollapsibleRow } from '../../../components';
import { useCommonStyles } from '../../../styles';

import { ColumnConfig } from './types';

interface Props {
  data: any[];
  columns: ColumnConfig[];
  groupByKey?: string;
  onRowAction?: (row: any) => void;
  rowActionIcon?: React.ReactNode;
  columnSelect?: boolean;
}

const parseTranslations = (translations?: string): Record<string, string> => {
  if (!translations) {
    return {};
  }

  try {
    return JSON.parse(translations);
  } catch {
    return {};
  }
};

export const DataTableBody: FC<Props> = ({ data, columns, groupByKey, onRowAction, rowActionIcon, columnSelect }) => {
  const commonClasses = useCommonStyles();
  const { translations } = useAppData();

  const translation = useMemo(() => parseTranslations(translations), [translations]);

  const renderCell = (col: ColumnConfig, row: any) => {
    const value = row[col.key];

    if (col.format) {
      return col.format(value, row);
    }

    if (value == null || value === '') {
      return '-';
    }

    return value;
  };

  const renderRow = (row: any, index: number) => (
    <TableRow className={commonClasses.tableRow} key={row.id ?? index}>
      {groupByKey && (
        <TableCell
          style={{
            width: 30,
            padding: '6px 16px',
          }}
        />
      )}

      {columns.map((col) => (
        <TableCell key={col.key} align={col.align || 'left'}>
          {renderCell(col, row)}
        </TableCell>
      ))}

      {onRowAction && (
        <TableCell align="center" className={commonClasses.btnCell}>
          <IconButton size="small" onClick={() => onRowAction(row)}>
            {rowActionIcon}
          </IconButton>
        </TableCell>
      )}

      {columnSelect && !onRowAction && <TableCell className={commonClasses.btnCell} align="center" />}
    </TableRow>
  );

  const renderTotalsRow = useMemo(() => {
    const hasSummable = columns.some((col) => col.summable);

    if (!hasSummable) {
      return null;
    }

    const totalCells = columns.map((col, index) => {
      if (col.summable) {
        const total = data.reduce((sum, row) => {
          const value = Number(row[col.key]);

          return Number.isNaN(value) ? sum : sum + value;
        }, 0);

        const display = col.format ? col.format(total, {} as any) : total.toFixed(2);

        return (
          <TableCell key={col.key} align={col.align || 'left'}>
            <strong>{display}</strong>
          </TableCell>
        );
      }

      if (index === 0) {
        return (
          <TableCell key={col.key} align={col.align || 'left'}>
            <strong>{translation.total || 'Total'}</strong>
          </TableCell>
        );
      }

      return <TableCell key={col.key} align={col.align || 'left'} />;
    });

    return (
      <TableRow
        className={commonClasses.tableRow}
        style={{
          backgroundColor: '#5e5b5b38',
        }}
      >
        {groupByKey && (
          <TableCell
            style={{
              width: 30,
              padding: '6px 16px',
            }}
          />
        )}

        {totalCells}

        {(onRowAction || columnSelect) && <TableCell className={commonClasses.btnCell} align="center" />}
      </TableRow>
    );
  }, [columns, data, commonClasses.tableRow, commonClasses.btnCell, onRowAction, columnSelect, groupByKey, translation.total]);

  const renderGroupedRows = () => {
    if (!groupByKey) {
      return null;
    }

    const grouped = groupBy(data, groupByKey);

    return Object.keys(grouped).map((groupValue) => {
      const subRows = grouped[groupValue];

      const renderedSubRows = subRows.map((row: any, index: number) => renderRow(row, index));

      const rowCells = columns.map((col) => (
        <TableCell key={col.key} align={col.align || 'left'}>
          {col.key === groupByKey ? groupValue : ''}
        </TableCell>
      ));

      if (onRowAction || columnSelect) {
        rowCells.push(<TableCell key="action" className={commonClasses.btnCell} align="center" />);
      }

      return <ReportsCollapsibleRow key={groupValue} rowCells={rowCells} subRows={renderedSubRows} columnsNumber={1 + columns.length + (onRowAction || columnSelect ? 1 : 0)} />;
    });
  };

  return (
    <>
      {renderTotalsRow}

      {groupByKey ? renderGroupedRows() : data.map((row, index) => renderRow(row, index))}
    </>
  );
};
